import React from 'react';
import { Box, TextField, IconButton, Button, Typography } from "@mui/material";
import { Trash as TrashIcon, PlusCircle as PlusCircleIcon } from "lucide-react";

// Editor for a single grammar concept
const GrammarConceptEditor = ({ conceptObj, index, onUpdate, onRemove }) => {
  // conceptObj: { concept, details, description, examples }
  const handleChange = (field, value) => {
    onUpdate(index, { ...conceptObj, [field]: value });
  };

  // Grammar info: dynamic key-value pairs
  const handleDetailChange = (detailIndex, field, value) => {
    const newDetails = [...(conceptObj.details || [])];
    newDetails[detailIndex] = { ...newDetails[detailIndex], [field]: value };
    onUpdate(index, { ...conceptObj, details: newDetails });
  };

  const handleAddDetail = () => {
    const newDetails = [...(conceptObj.details || []), { key: '', value: '' }];
    onUpdate(index, { ...conceptObj, details: newDetails });
  };

  const handleRemoveDetail = (detailIndex) => {
    const newDetails = (conceptObj.details || []).filter((_, i) => i !== detailIndex);
    onUpdate(index, { ...conceptObj, details: newDetails });
  };

  // Examples: each with foreign and native sentences
  const handleExampleChange = (exampleIndex, field, value) => {
    const newExamples = [...(conceptObj.examples || [])];
    newExamples[exampleIndex] = { ...newExamples[exampleIndex], [field]: value };
    onUpdate(index, { ...conceptObj, examples: newExamples });
  };

  const handleAddExample = () => {
    const newExamples = [...(conceptObj.examples || []), { foreignSentence: '', nativeSentence: '' }];
    onUpdate(index, { ...conceptObj, examples: newExamples });
  };

  const handleRemoveExample = (exampleIndex) => {
    const newExamples = (conceptObj.examples || []).filter((_, i) => i !== exampleIndex);
    onUpdate(index, { ...conceptObj, examples: newExamples });
  };

  return (
    <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <TextField
          label="Concept"
          value={conceptObj.concept}
          onChange={(e) => handleChange('concept', e.target.value)}
          fullWidth
          size="small"
        />
        <IconButton color="error" onClick={() => onRemove(index)}>
          <TrashIcon size={16} />
        </IconButton>
      </Box>
      {/* Rename Details as Grammar info */}
      <Typography variant="subtitle2">Grammar info:</Typography>
      {(conceptObj.details || []).map((detail, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField
            label="Key"
            value={detail.key}
            onChange={(e) => handleDetailChange(i, 'key', e.target.value)}
            size="small"
          />
          <TextField
            label="Value"
            value={detail.value}
            onChange={(e) => handleDetailChange(i, 'value', e.target.value)}
            size="small"
          />
          <IconButton color="error" onClick={() => handleRemoveDetail(i)}>
            <TrashIcon size={16} />
          </IconButton>
        </Box>
      ))}
      <Button variant="outlined" startIcon={<PlusCircleIcon size={16} />} onClick={handleAddDetail} sx={{ mb: 2 }}>
        Add Detail
      </Button>
      {/* Description field */}
      <TextField
        label="Description"
        value={conceptObj.description}
        onChange={(e) => handleChange('description', e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ mb: 2 }}
      />
      {/* Examples subform */}
      <Typography variant="subtitle2">Example Sentences:</Typography>
      {(conceptObj.examples || []).map((example, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <TextField
            label="Foreign Sentence"
            value={example.foreignSentence}
            onChange={(e) => handleExampleChange(i, 'foreignSentence', e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Native Sentence"
            value={example.nativeSentence}
            onChange={(e) => handleExampleChange(i, 'nativeSentence', e.target.value)}
            size="small"
            fullWidth
          />
          <IconButton color="error" onClick={() => handleRemoveExample(i)}>
            <TrashIcon size={16} />
          </IconButton>
        </Box>
      ))}
      <Button variant="outlined" startIcon={<PlusCircleIcon size={16} />} onClick={handleAddExample}>
        Add Example
      </Button>
    </Box>
  );
};

const GrammarConceptsEditor = ({ grammarConcepts, onChange }) => {
  const handleUpdate = (index, updatedConcept) => {
    const newConcepts = [...grammarConcepts];
    newConcepts[index] = updatedConcept;
    onChange(newConcepts);
  };

  const handleRemove = (index) => {
    const newConcepts = grammarConcepts.filter((_, i) => i !== index);
    onChange(newConcepts);
  };

  const handleAdd = () => {
    // Prepopulate Grammar info with two key-value pairs
    onChange([
      ...grammarConcepts,
      { 
        concept: '', 
        details: [
          { key: 'Part of Speech', value: '' },
          { key: 'Word Type', value: '' }
        ], 
        description: '', 
        examples: [] 
      }
    ]);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {grammarConcepts.map((conceptObj, index) => (
        <GrammarConceptEditor 
          key={index}
          conceptObj={conceptObj}
          index={index}
          onUpdate={handleUpdate}
          onRemove={handleRemove}
        />
      ))}
      <Button variant="outlined" startIcon={<PlusCircleIcon size={16} />} onClick={handleAdd}>
        Add Grammar Concept
      </Button>
    </Box>
  );
};

export default GrammarConceptsEditor;
